// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The pool of light a machine throws on the floor it is standing on (M9-51, M5-19, 06 §3).
//
// **`lightingMatch.ts` has described this since M5-19 and nothing drew it.** Its header names two
// effects and calls this one first: *"A pinball machine is a light source — a lit playfield, a
// backglass, flashers — and in passthrough the thing that most gives away a composited object is
// that it does not affect anything around it. A soft pool of the machine's own colour on the real
// floor is cheap and does most of the work."* `LightingMatch.spill` was computed every frame on
// both clients, tested on both clients, and read by neither.
//
// **A texture rather than a light.** A real light spilling onto a real floor is not something a
// renderer can do — there is no floor in the scene to light, only passthrough video the compositor
// owns — so the pool is drawn *additively*, as a soft ellipse under the cabinet with no depth
// write. Additive is what makes it read as light rather than as a decal: it brightens whatever the
// compositor has behind it and leaves black where it is black, which is exactly what a pool of
// light does and what a shadow-shaped sprite does not.
//
// **Generated in code**, like every other asset in this product: a radial gradient computed into a
// byte array once, at the moment the scene is built. No file, no licence, no upload surface.
//
// **A `DataTexture` and not a canvas**, which is a correctness decision rather than a style one.
// The first version painted a `createRadialGradient` into a `<canvas>`, so it returned *nothing*
// outside a browser — and the pool was therefore absent from every test in this repository, which
// runs Vitest with `environment: 'node'`. A feature that cannot exist where it is checked is a
// feature nothing checks. Arithmetic into a `Uint8Array` has no such seam, is shorter, and works
// identically in both places.
//
// **Elliptical, because a cabinet is.** A circular pool under a body two and a half times longer
// than it is wide reads as a spotlight somebody aimed, which is the opposite of the effect. The
// plane is stretched to the footprint and the gradient is drawn round.

import {
  AdditiveBlending,
  DataTexture,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  RGBAFormat,
  SRGBColorSpace,
  UnsignedByteType,
  type BufferGeometry,
  type Material,
} from 'three'

/** What the pool is made of, so the scene can dispose exactly what it built. */
export interface BuiltSpill {
  readonly mesh: Mesh
  readonly material: MeshBasicMaterial
  readonly geometries: readonly BufferGeometry[]
  readonly materials: readonly Material[]
  readonly textures: readonly DataTexture[]
}

/**
 * How far past the cabinet's own footprint the pool reaches, as a multiple of it.
 *
 * 1.9. A machine's light does not stop at its own feet, and a pool clipped to the footprint reads
 * as a mat rather than as light. Wide enough to be soft, small enough that a player standing at
 * the machine is not inside it.
 */
export const SPILL_SPREAD = 1.9

/**
 * How far above the floor the plane sits, metres.
 *
 * 5 mm. Zero z-fights with a real floor the moment the runtime's floor estimate moves — and this
 * repository has a headset reporting eye heights of 3.44 m and −2.63 m in one session, so the
 * floor estimate moving is the expected case rather than the unlucky one.
 */
export const SPILL_LIFT = 0.005

/** Texture resolution. A gradient with no detail in it; 128 is already more than it needs. */
const POOL_PIXELS = 128

/** The pool, at the cabinet's footprint. */
export function buildLightSpill(width: number, length: number): BuiltSpill {
  const texture = pool()
  const geometry = new PlaneGeometry(width * SPILL_SPREAD, length * SPILL_SPREAD)
  const material = new MeshBasicMaterial({
    map: texture,
    transparent: true,
    // **Additive, and `depthWrite` off.** It brightens what is behind it rather than covering it,
    // which is the difference between a pool of light and a sticker on the floor — and a
    // transparent plane that wrote depth would punch a hole in everything drawn after it.
    blending: AdditiveBlending,
    depthWrite: false,
    opacity: 0,
  })
  const mesh = new Mesh(geometry, material)
  // Flat on the floor. The plane is built in XY and the floor is XZ, so it turns a quarter turn
  // about X — and it hangs off the machine's own root, so a machine carried across the room takes
  // its pool with it.
  mesh.rotation.x = -Math.PI / 2
  mesh.position.y = SPILL_LIFT
  // Drawn after the cabinet, so an additive blend has the cabinet's own pixels behind it where
  // they overlap rather than being drawn into an empty buffer.
  mesh.renderOrder = 1
  mesh.visible = false
  mesh.name = SPILL_NAME
  return { mesh, material, geometries: [geometry], materials: [material], textures: [texture] }
}

/** What the mesh is called, so a scene can find its own pool without a second reference to it. */
export const SPILL_NAME = 'light-spill'

/**
 * The gradient itself: bright in the middle, nothing at the edge.
 *
 * A **quadratic** falloff rather than a linear one. Linear leaves a visible ring where the pool
 * ends, which on a real floor in passthrough is the single most artificial thing this effect could
 * produce; `(1 − t)²` takes the edge to zero with a zero slope, so it disappears into the room.
 *
 * Clamped past the corners: the square texture's corners are 1.41 radii out, and without the clamp
 * `(1 − t)²` turns back *upward* there and puts four bright spots outside the pool.
 */
function pool(): DataTexture {
  const half = POOL_PIXELS / 2
  const pixels = new Uint8Array(POOL_PIXELS * POOL_PIXELS * 4)
  for (let y = 0; y < POOL_PIXELS; y++) {
    for (let x = 0; x < POOL_PIXELS; x++) {
      // +0.5 to sample the centre of the texel rather than its corner, so the pool is symmetric
      // about the middle of the image instead of half a texel off it.
      const dx = (x + 0.5 - half) / half
      const dy = (y + 0.5 - half) / half
      const distance = Math.sqrt(dx * dx + dy * dy)
      const t = distance > 1 ? 1 : distance
      const level = Math.round(255 * (1 - t) * (1 - t))
      const at = (y * POOL_PIXELS + x) * 4
      pixels[at] = level
      pixels[at + 1] = level
      pixels[at + 2] = level
      pixels[at + 3] = 255
    }
  }
  const texture = new DataTexture(pixels, POOL_PIXELS, POOL_PIXELS, RGBAFormat, UnsignedByteType)
  texture.colorSpace = SRGBColorSpace
  texture.needsUpdate = true
  // No mipmaps: the pool is a smooth gradient viewed at one scale, and generating a chain for it
  // is memory spent on a picture that has no detail to lose.
  texture.generateMipmaps = false
  texture.minFilter = LinearFilter
  texture.magFilter = LinearFilter
  return texture
}
